import React from 'react';
import type { PopulatedLead } from '../types';
import type { LeadCardAction } from './LeadCard';
import { Avatar, AvatarImage, AvatarFallback, CardActionsMenu, StatusPill } from '@/shared/components/ui';
import { PrivacyText } from '@/shared/components/ui/PrivacyText';
import { Icon } from '@/shared/icons';
import { LeadPriorityPill } from './LeadListRow';
import { buildLeadCardActions } from './leadCardActions';
import {
  formatLeadCurrency,
  getLeadInitials,
  getLeadPriorityMeta,
  getBusinessBadge,
  getBusinessPillVariant,
  hasLeadAlert,
} from './leadRowHelpers';

/**
 * Card da listagem de leads (view "Cards" + mobile).
 * Padrão-lista F34: card SEM checkbox (seleção em massa vive só na view de lista)
 * + kebab ••• `CardActionsMenu` shared sempre visível (F36/F77).
 */
export interface LeadGridCardProps {
  lead: PopulatedLead;
  selected: boolean;
  onAction: (action: LeadCardAction, lead: PopulatedLead) => void;
}

export const LeadGridCard: React.FC<LeadGridCardProps> = ({ lead, selected, onAction }) => {
  const priority = getLeadPriorityMeta(lead.priority);
  const business = getBusinessBadge(lead.businessStatus);
  const alert = hasLeadAlert(lead);

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-card-border)',
      }}
      data-testid={`lead-card-${lead.id}`}
    >
      {/* `.ucard-top`: avatar 40 + nome 15 bold / "Funil · Etapa" 12 muted + sino(alerta) + ⋮ */}
      <div className="flex items-center gap-[11px]">
        <Avatar className="w-10 h-10 flex-shrink-0" isSquircle>
          {lead.contact?.picture && <AvatarImage src={lead.contact.picture} alt={lead.contact?.name || ''} isSquircle />}
          <AvatarFallback isSquircle>
            <span className="text-white font-bold text-sm">{getLeadInitials(lead.contact?.name)}</span>
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-[15px] leading-[1.25] truncate" style={{ color: 'var(--theme-card-text)' }}>
            <PrivacyText>{lead.contact?.name || 'Sem nome'}</PrivacyText>
          </h3>
          <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
            {(lead.funnel?.name || '—')}{lead.step?.name ? ` · ${lead.step.name}` : ''}
          </p>
        </div>

        {alert && <Icon name="bell" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#ef4444' }} />}
        <div className="flex-shrink-0">
          <CardActionsMenu
            size="sm"
            actions={buildLeadCardActions(lead, (action, l) => onAction(action, l as PopulatedLead))}
            data-testid={`lead-actions-button-${lead.id}`}
          />
        </div>
      </div>

      {/* `.ucard-foot`: pbadge prioridade + StatusPill status + valor bold à direita */}
      <div className="mt-[11px] flex items-center flex-wrap gap-[7px]">
        <LeadPriorityPill color={priority.color} label={priority.label} />
        <StatusPill variant={getBusinessPillVariant(lead.businessStatus)} label={business.label} />
        <span className="ml-auto text-sm font-bold tabular-nums flex-shrink-0" style={{ color: 'var(--theme-card-text)' }}>
          {formatLeadCurrency(lead.budget)}
        </span>
      </div>
    </div>
  );
};

LeadGridCard.displayName = 'LeadGridCard';
